"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useConfiguredApolloClient = useConfiguredApolloClient;
exports.createApolloClient = createApolloClient;
const client_1 = require("@apollo/client");
const context_1 = require("@apollo/client/link/context");
const error_1 = require("@apollo/client/link/error");
const persisted_queries_1 = require("@apollo/client/link/persisted-queries");
const retry_1 = require("@apollo/client/link/retry");
const app_utils_1 = require("@sixfold/app-utils");
const typed_primitives_1 = require("@sixfold/typed-primitives");
const apollo_link_sentry_1 = require("apollo-link-sentry");
const react_1 = require("react");
const utils_1 = require("./utils");
// We expose all of Apollo as well, to avoid someone needing to go directly to it
__exportStar(require("@apollo/client"), exports);
function useConfiguredApolloClient(config) {
    // As useComparisonEffect is called on mount as well, we want to skip that part, for us that is simple to track
    const initialMountDone = (0, react_1.useRef)(false);
    // We initial the state with a function, that way we don't create random clients on every render of this hook
    const [client, setClient] = (0, react_1.useState)(() => createApolloClient(config));
    (0, app_utils_1.useComparisonEffect)(() => {
        if (!initialMountDone.current) {
            initialMountDone.current = true;
            return;
        }
        setClient(createApolloClient(config));
    }, [
        config.baseURL,
        config.getAuthorizationToken,
        config.onAuthenticationError,
        config.onReportError,
        config.getHTTPLink,
        config.possibleTypes,
        config.typePolicies,
        config.enablePersistedQueries,
        config.defaultOptions,
    ], (a, b) => (0, typed_primitives_1.deepEqual)(a, b));
    // Periodic cache cleanup
    (0, app_utils_1.useActiveUserInterval)(() => {
        client.cache.gc();
    }, { activeInterval: 300000, idleInterval: 3600000 });
    return client;
}
function createApolloClient({ baseURL, path = '/graphql', getHTTPLink, getAuthorizationToken, onAuthenticationError, onReportError, possibleTypes, typePolicies, retryConfig = { initial: 500, max: 30000, jitter: true }, enablePersistedQueries = true, defaultOptions, }) {
    const authLink = (0, context_1.setContext)((_, { headers }) => {
        const token = getAuthorizationToken?.();
        if (token === undefined) {
            return { headers };
        }
        if (typeof token === 'string') {
            return { headers: { ...headers, authorization: `Bearer ${token}` } };
        }
        return { headers: { ...headers, authorization: `${token.type} ${token.value}` } };
    });
    const retryLink = new retry_1.RetryLink({
        delay: retryConfig,
        attempts: (count, _, error) => {
            if (error === undefined) {
                return false;
            }
            if (isServerError(error) || isParseError(error)) {
                // Retry if not a 4xx error
                return error.statusCode % 400 >= 100;
            }
            if (error.statusCode !== undefined && error.statusCode % 400 < 100) {
                // Never retry 4xx errors
                return false;
            }
            if (isNetworkConnectionError(error)) {
                // Always retry connectivity related errors
                return true;
            }
            // Only retry 5 times for other errors
            return count < 5;
        },
    });
    const trimmedPath = path.startsWith('/') ? path.slice(1) : path;
    const sentryLink = new apollo_link_sentry_1.SentryLink({
        attachBreadcrumbs: {
            includeQuery: true,
            includeVariables: true,
            includeFetchResult: false,
        },
    });
    const httpLink = (0, client_1.createHttpLink)({
        uri: baseURL !== undefined ? (baseURL.endsWith('/') ? `${baseURL}${trimmedPath}` : `${baseURL}/${trimmedPath}`) : path,
        credentials: 'same-origin',
    });
    const link = client_1.ApolloLink.from([
        retryLink,
        sentryLink,
        (0, error_1.onError)(({ operation, graphQLErrors, networkError }) => {
            // We can have an auth error two ways, either a 401 from API, or
            // 'Authentication Error' as a GraphQL error
            const networkAuthError = (0, typed_primitives_1.notNil)(networkError) && isErrorWithStatusCode(networkError) && networkError.statusCode === 401
                ? networkError
                : undefined;
            const authError = networkAuthError ?? graphQLErrors?.find((error) => error.message === 'Authentication Error');
            if (authError !== undefined && onAuthenticationError !== undefined) {
                onAuthenticationError(authError);
            }
            // Report errors when we don't have an auth error
            if (authError === undefined) {
                reportGraphQLErrors(operation, graphQLErrors, onReportError);
                reportNetworkError(operation, networkError, onReportError);
            }
        }),
        authLink,
        ...(enablePersistedQueries ? [(0, persisted_queries_1.createPersistedQueryLink)({ sha256: utils_1.sha256 })] : []),
        getHTTPLink?.(httpLink) ?? httpLink,
    ]);
    return new client_1.ApolloClient({
        link,
        cache: new client_1.InMemoryCache({ dataIdFromObject: utils_1.dataIdFromObject, possibleTypes, typePolicies }),
        defaultOptions,
    });
}
function reportGraphQLErrors(operation, errors, reportError) {
    if (errors === undefined || errors.length === 0) {
        return;
    }
    const mappedErrors = errors.map((e) => ({ ...e, name: 'GraphQLError' }));
    const aggregatedError = new typed_primitives_1.AggregateError([...mappedErrors]);
    reportError?.(aggregatedError, {
        errors: errors.map(({ message, locations }) => ({
            message,
            operationName: operation.operationName,
            variables: operation.variables,
            extensions: operation.extensions,
            locations: locations !== undefined ? locations.map(({ line, column }) => ({ line, column })) : undefined,
        })),
    });
}
function reportNetworkError(operation, error, reportError) {
    if ((0, typed_primitives_1.isNil)(error)) {
        return;
    }
    const extra = {
        operationName: operation.operationName,
        variables: operation.variables,
        extensions: operation.extensions,
    };
    if (isServerError(error)) {
        reportError?.(error, { statusCode: error.statusCode, ...extra });
    }
    else if (isParseError(error)) {
        reportError?.(error, {
            statusCode: error.statusCode,
            bodyText: error.bodyText,
            ...extra,
        });
    }
    else {
        reportError?.(error, { ...extra });
    }
}
function isServerError(error) {
    return error.statusCode !== undefined && error.result !== undefined;
}
function isParseError(error) {
    return error.bodyText !== undefined;
}
function isErrorWithStatusCode(error) {
    return error.statusCode !== undefined;
}
// Based on gist https://gist.github.com/keeth/d5d94dab40b4d697618c075e155197da
function isNetworkConnectionError(error) {
    return (error.statusCode === 502 ||
        error.statusCode === 504 ||
        /The Internet connection appears to be offline|Failed to fetch|Network request failed|NetworkError when attempting to fetch resource|The network connection was lost/.test(error.message));
}
//# sourceMappingURL=client.js.map